import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { upsertPrimeCompany, type PrimeCompany } from "../api/CrmApi";
import EditCompanyNotesModal from "./EditCompanyNotesModal";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  companyId: number;
  notes: string;
  onSaved: (company: PrimeCompany) => void;
}

/** Each stored note is just one line of free text — no date format is
 * enforced or assumed, so this simply splits on newlines rather than trying
 * to parse a "[date] ..." convention that not every line will follow (manual
 * edits, older freeform notes, etc). Shown in exactly the order the text
 * splits into — no re-sorting. A new note is prepended to the top of the
 * stored text (newest line first), unlike /rfp-prime-reply-analysis' own
 * append-at-the-end convention — the two write paths intentionally order
 * differently for now. */
export default function CompanyNotesSection({ companyId, notes, onSaved }: Props) {
  const [noteInput, setNoteInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [rawEditOpen, setRawEditOpen] = useState(false);

  const entries = notes
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  async function addNote() {
    const text = noteInput.trim();
    if (!text) return;
    setAdding(true);
    setAddError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const newLine = `[${today}] ${text}`;
      const merged = notes.trim() ? `${newLine}\n${notes.trim()}` : newLine;
      // Only `notes` is sent — this never touches naics_codes, so it can't
      // trip the "at least one NAICS code" rule that only applies when a
      // caller actually touches naics_codes (see upsert_prime_company).
      const updated = await upsertPrimeCompany({ company_id: companyId, notes: merged });
      onSaved(updated);
      setNoteInput("");
    } catch (err: any) {
      setAddError(err?.response?.data?.detail || err?.message || "Failed to add note. Please try again.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-medium text-muted-foreground">Notes</label>
        <button
          type="button"
          onClick={() => setRawEditOpen(true)}
          title="Edit the full notes text directly — e.g. to fix a typo in an old entry"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <Pencil className="w-3 h-3" /> Edit raw text
        </button>
      </div>

      <div className="space-y-1.5 mb-2 max-h-56 overflow-y-auto">
        {entries.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No notes yet.</p>
        ) : (
          entries.map((line, i) => (
            <div key={i} className="bg-surface border border-border rounded-lg px-3 py-2 text-xs text-foreground">
              {line}
            </div>
          ))
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }}
          disabled={adding}
          placeholder="Add a note — press Enter or click +"
          className={`flex-1 px-3 py-2 ${FORM_CONTROL}`}
        />
        <button
          type="button"
          onClick={addNote}
          disabled={adding || !noteInput.trim()}
          className="p-2 rounded-lg border border-border bg-surface text-foreground hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {addError && (
        <Alert tone="danger" className="mt-2">{addError}</Alert>
      )}

      {rawEditOpen && (
        <EditCompanyNotesModal
          companyId={companyId}
          notes={notes}
          onClose={() => setRawEditOpen(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
